import React, { useState } from 'react';
import { Calendar, Clock, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { EuroDatePicker } from '@/components/ui/euro-date-picker';

const DateTimePickerCard = ({ onSelect, initialDate, initialTime }: any) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(initialDate || '');
  const [selectedTime, setSelectedTime] = useState(initialTime || '');

  const handleDateChange = (val: string) => {
    setSelectedDate(val);
    if (selectedTime) {
      onSelect({ date: val, time: selectedTime });
    }
  };

  const handleTimeChange = (e: any) => {
    const time = e.target.value;
    setSelectedTime(time);
    if (selectedDate) {
      onSelect({ date: selectedDate, time });
    }
  };

  const isComplete = selectedDate && selectedTime;

  // Get min date (today)
  const today = new Date().toISOString().split('T')[0];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-4"
    >
      <div className="relative group">
        <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-300"></div>
        
        <div className="relative bg-background dark:bg-slate-800 rounded-2xl p-4 border border-border dark:border-slate-700">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <Calendar size={20} className="text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground dark:text-slate-200">{t('dateTimePickerCard.datoOgTid', 'Dato og tid')}</h4>
                <p className="text-xs text-muted-foreground">{t('auto.nr_skal_transporten_vre', 'When should the transport be?')}</p>
              </div>
            </div>
            {isComplete && (
              <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Check size={16} className="text-green-600 dark:text-green-400" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Date picker */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground dark:text-muted-foreground mb-2">
                📅 Dato
              </label>
              <EuroDatePicker
                value={selectedDate}
                onChange={handleDateChange}
                min={today}
                className="w-full px-4 py-3 bg-muted/50 dark:bg-slate-900/50 border border-border dark:border-slate-700 rounded-xl"
              />
            </div>

            {/* Time picker */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground dark:text-muted-foreground mb-2">
                🕐 Tidspunkt
              </label>
              <input
                type="time"
                value={selectedTime}
                onChange={handleTimeChange}
                className="w-full px-4 py-3 bg-muted/50 dark:bg-slate-900/50 border border-border dark:border-slate-700 rounded-xl text-foreground dark:text-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          {isComplete && (
            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                ✓ Valgt: {new Date(selectedDate).toLocaleDateString('no-NO', { 
                  weekday: 'long', 
                  day: 'numeric', 
                  month: 'long' 
                })} kl. {selectedTime}
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default DateTimePickerCard;
